// src/Hooks/useMailingListManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { db } from '../Firebase/firebase';
import { UnsubscribeService } from '../Components/Unsubscribe/UnsubscribeService';

export function useMailingListManage() {
  const [activeAction, setActiveAction] = useState('VIEW');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [dbRows, setDbRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [regions, setRegions] = useState([]);

  // --- SORTING STATE ---
  const [sortConfig, setSortConfig] = useState({ key: 'userName', direction: 'asc' });

  // --- DATE FILTER STATE ---
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);

  const [showResultAlert, setShowResultAlert] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const [exportSettings, setExportSettings] = useState(() => {
    const saved = sessionStorage.getItem('mailingListExportSettings');
    return saved ? JSON.parse(saved) : { selectedRegionIds: [], blockSize: 50 };
  });

  useEffect(() => {
    sessionStorage.setItem('mailingListExportSettings', JSON.stringify(exportSettings));
  }, [exportSettings]);

  useEffect(() => {
    const unsubMailingList = onSnapshot(collection(db, 'mailingList'), (snapshot) => {
      const fetchedData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setDbRows(fetchedData);
      setLoading(false);
    });

    const unsubRegions = onSnapshot(collection(db, 'region'), (snapshot) => {
      let fetchedRegions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      fetchedRegions = fetchedRegions.filter((r) => !r.isArchived);
      fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setRegions(fetchedRegions);
    });

    return () => {
      unsubMailingList();
      unsubRegions();
    };
  }, []);

  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) =>
      edits[row.id] ? { ...row, ...edits[row.id] } : row,
    );
    const newRowIds = new Set(newRows.map((r) => r.id));
    let combinedRows = [...newRows, ...mergedDbRows.filter((r) => !newRowIds.has(r.id))];

    // --- DATE FILTER LOGIC ---
    if (dateFrom || dateTo) {
      const fromTime = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : 0;
      const toTime = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : Infinity;

      combinedRows = combinedRows.filter((row) => {
        if (!row.addedAt) return true; // Keep rows without dates
        const rowTime = new Date(row.addedAt).getTime();
        if (isNaN(rowTime)) return true; // Keep rows with invalid dates
        return rowTime >= fromTime && rowTime <= toTime;
      });
    }

    if (sortConfig.key) {
      combinedRows.sort((a, b) => {
        let valA = a[sortConfig.key] || '';
        let valB = b[sortConfig.key] || '';

        if (sortConfig.key === 'addedAt') {
          if (!a[sortConfig.key] && b[sortConfig.key])
            return sortConfig.direction === 'asc' ? 1 : -1;
          if (a[sortConfig.key] && !b[sortConfig.key])
            return sortConfig.direction === 'asc' ? -1 : 1;
          if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;

          valA = new Date(a[sortConfig.key]).getTime();
          valB = new Date(b[sortConfig.key]).getTime();

          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return combinedRows;
  }, [dbRows, edits, newRows, sortConfig, dateFrom, dateTo]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const executeDownload = useCallback(() => {
    const { selectedRegionIds, blockSize } = exportSettings;
    if (selectedRegionIds.length === 0) return;

    const filteredEmails = displayRows
      .filter((row) => selectedRegionIds.includes(row.regionId))
      .map((row) => row.email)
      .filter((email) => !!email);

    if (filteredEmails.length === 0) {
      setResultMessage('No emails found for the selected regions.');
      setResultSeverity('info');
      setShowResultAlert(true);
      return;
    }

    const size = parseInt(blockSize) || 50;
    let fileIndex = 1;

    for (let i = 0; i < filteredEmails.length; i += size) {
      const chunk = filteredEmails.slice(i, i + size);
      const content = chunk.join('; ');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Mailing_List_${fileIndex}.txt`;
      link.click();
      URL.revokeObjectURL(url);
      fileIndex++;
    }
    setDownloadDialogOpen(false);
  }, [displayRows, exportSettings]);

  const handleAddNew = () => {
    const newId = `temp_${Date.now().toString()}`;
    setNewRows((prev) => [
      ...prev,
      { id: newId, userName: '', companyName: '', email: '', regionId: '' },
    ]);
    setActiveAction('EDIT_MODE');
    setFocusedRowId(newId);
  };

  const handleDeleteRequest = () => {
    if (selectedIds.length === 0) return;
    setShowDeleteAlert(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteAlert(false);
    setLoading(true);

    const localIdsToDelete = [];
    const dbIdsToDelete = [];

    selectedIds.forEach((id) => {
      if (newRows.some((r) => r.id === id)) localIdsToDelete.push(id);
      else dbIdsToDelete.push(id);
    });

    if (localIdsToDelete.length > 0) {
      setNewRows((prev) => prev.filter((r) => !localIdsToDelete.includes(r.id)));
    }

    if (dbIdsToDelete.length > 0) {
      const usersToDelete = dbRows
        .filter((row) => dbIdsToDelete.includes(row.id))
        .map((row) => ({
          id: row.id,
          email: row.email,
          userName: row.userName,
          companyName: row.companyName || '',
        }));

      try {
        const result = await UnsubscribeService.execute(usersToDelete, {
          isSelf: false,
          addToAudit: false,
          skipAuth: true,
        });

        if (result.failCount === 0) {
          setResultSeverity('success');
          setResultMessage(
            `${result.successCount} users have been successfully deleted from the list.`,
          );
        } else {
          setResultSeverity('warning');
          setResultMessage(
            `Done. Success: ${result.successCount}, Failed: ${result.failCount}. Reason: ${result.failReason}`,
          );
        }
        setShowResultAlert(true);
      } catch (error) {
        console.error('Delete Service Failed:', error);
        setResultSeverity('error');
        setResultMessage('An error occurred during deletion.');
        setShowResultAlert(true);
      }
    }

    setLoading(false);
    setSelectedIds([]);
  };

  const processImport = async (rows) => {
    setLoading(true);
    try {
      const batch = writeBatch(db);
      let addedCount = 0;
      let updatedCount = 0;

      rows.forEach((row) => {
        const cleanEmail = String(row.email).toLowerCase().trim();
        if (!cleanEmail) return;

        const existingRow = dbRows.find(
          (r) => r.email && r.email.toLowerCase().trim() === cleanEmail,
        );

        if (existingRow) {
          const docRef = doc(db, 'mailingList', existingRow.id);
          batch.update(docRef, {
            userName: row.userName || existingRow.userName || '',
            companyName: row.companyName || existingRow.companyName || '',
          });

          updatedCount++;
        } else {
          const newDocRef = doc(collection(db, 'mailingList'));

          batch.set(newDocRef, {
            userName: row.userName || '',
            companyName: row.companyName || '',
            email: cleanEmail,
            addedAt: new Date().toISOString(),
          });

          addedCount++;
        }
      });

      await batch.commit();
      setResultMessage(`Import complete: ${addedCount} added, ${updatedCount} updated.`);
      setResultSeverity('success');
      setShowResultAlert(true);
    } catch (error) {
      console.error('Import processing error:', error);
      setResultMessage('An error occurred while uploading the data to the server.');
      setResultSeverity('error');
      setShowResultAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return {
    state: {
      displayRows,
      selectedIds,
      loading,
      focusedRowId,
      regions,
      downloadDialogOpen,
      exportSettings,
      sortConfig,
      dateFrom,
      dateTo,
    },
    flags: {
      isDirty: newRows.length > 0 || Object.keys(edits).length > 0,
      canDelete: selectedIds.length > 0,
      canSave: newRows.length > 0 || Object.keys(edits).length > 0,
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
      showResultAlert,
      setShowResultAlert,
      resultMessage,
      setResultMessage,
      resultSeverity,
      setResultSeverity,
    },
    actions: {
      handleAddNew,
      handleDownload: () => setDownloadDialogOpen(true),
      setDownloadDialogOpen,
      setExportSettings,
      executeDownload,
      processImport,
      handleDeleteRequest,
      handleConfirmDelete,
      requestSort,
      setDateFrom,
      setDateTo,
      handleSave: async () => {
        const batch = writeBatch(db);

        newRows.forEach((row) => {
          if (row.email && row.email.trim() !== '') {
            const cleanEmail = row.email.toLowerCase().trim();

            const existingRow = dbRows.find(
              (r) => r.email && r.email.toLowerCase().trim() === cleanEmail,
            );

            if (existingRow) {
              const docRef = doc(db, 'mailingList', existingRow.id);
              batch.update(docRef, {
                userName: row.userName || existingRow.userName || '',
                companyName: row.companyName || existingRow.companyName || '',
              });
            } else {
              const newDocRef = doc(collection(db, 'mailingList'));

              batch.set(newDocRef, {
                userName: row.userName || '',
                companyName: row.companyName || '',
                email: cleanEmail,
                addedAt: new Date().toISOString(),
              });
            }
          }
        });

        Object.keys(edits).forEach((id) => {
          const editsPayload = edits[id];
          batch.update(doc(db, 'mailingList', id), editsPayload);
        });

        await batch.commit();
        setNewRows([]);
        setEdits({});
        setActiveAction('VIEW');
      },
      handleCheckboxChange: (id) =>
        setSelectedIds((prev) =>
          prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        ),
      handleSelectAll: (checked) => {
        if (checked) {
          setSelectedIds(displayRows.map((row) => row.id));
        } else {
          setSelectedIds([]);
        }
      },
      handleFieldChange: (id, field, val) => {
        if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');
        if (newRows.some((r) => r.id === id))
          setNewRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)),
          );
        else
          setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: val } }));
      },
      setFocusedRowId,
      resetInterface: () => {
        setNewRows([]);
        setEdits({});
        setSelectedIds([]);
        setShowUnsavedAlert(false);
        setActiveAction('VIEW');
      },
    },
  };
}
